/**
 * isgTaahhutnamePdf.js — Firma İSG Taahhütnamesi (file type 79) as a PDF, Turkish or English (backlog #541).
 *
 * Two different templates, as staff keep them (A4, measured with pdf.js), chosen by the language of the PDF:
 *   - "en" = "Yabancı Katılımcı İSG Taahhütnamesi EN.pdf": the fair logo top left, the SAKA GRUP logo top right, an event table (fair name,
 *     the three phases and their period), an exhibitor table (company, hall, booth), the undertaking, the acknowledgement, the signature box.
 *   - "tr" = "YERLİ FİRMA için İSG FİRMA TAAHHÜTNAMESİ": logos, a two-row table (company, hall / booth), the undertaking as running text that
 *     names the fair, its dates and its venue, the four documents the company must hold, three signature captions and a letterhead at the
 *     foot whose first two lines (the fair's ORGANIZER and its address) are FILLABLE PDF fields. The organizer's name is the fair's organizer
 *     firma (`organizatorAdlari`; it also stands in the last paragraph) and fills the first field; the address is stored nowhere, so that
 *     field is empty. A fair without an organizer firma says "Fuar organizatörü" and leaves both fields empty. Nothing is hardcoded to one organizer.
 * Every cell that used to be blank is filled from `veri`; a value that is not known stays blank so the company can still write it in.
 *
 * No network here: `veri` and `varliklar` come from isgTaahhutnameService.js (and the tests). pdf-lib and fontkit are imported lazily,
 * like pdfjs in pdfOku.js, so they only cost a download when a form is made. The wording lives in locales/<lang>/pdf.json and follows
 * `dil` (the language the PDF is made in), never the UI language.
 *
 * veri = { firmaAdi, fuarAdlari: { tr, en }, fuarAlani (the venue's name), organizatorAdlari: string[], asamalar: { kurulum, fuar, sokum } (each { ilk: {tarih, saat},
 *          son: {tarih, saat} } as in Components/Fairs/fuarAsamalari.js), holAdlari: string[], standKodlari: string[],
 *          standlar: [{ hol, kod }], logo: Uint8Array (PNG / JPEG) | null }
 * varliklar = { font: Uint8Array (TrueType with Latin Extended-A for ğ ş ı İ), sakaLogo: Uint8Array (PNG),
 *               fontKalin, fontItalik: Uint8Array (the bold and italic of the same family; the "tr" template needs them) }
 */
import i18n from "../i18n";

export const ISG_TAAHHUTNAME_DILLERI = ["tr", "en"];

// ── page geometry, in points from the TOP-left (the template's own numbers, converted from PDF's bottom-left origin) ──
const SAYFA = { en: 595.28, boy: 841.89 };
const KOL_SOL = 71.1; // left edge of table column 0
const KOL_GENISLIK = 113.28; // four equal columns: 71.1 / 184.4 / 297.7 / 410.9 / 524.2
const SAG = KOL_SOL + 4 * KOL_GENISLIK;
const FUAR_LOGO = { x: 68.7, ust: 47.3, en: 117.7, boy: 63.8 };
const SAKA_LOGO = { x: 419.6, ust: 44.9, en: 124.6, boy: 61.9 };
const TABLO1_UST = 154.2;
const TABLO_ARASI = 28.6;
const HUCRE_YATAY = 5.4;
const HUCRE_DIKEY = 3;
const HUCRE_EN_AZ = 15;
const TABLO_PUNTO = 12;
const METIN_PUNTO = 10.6; // measured from the template: its 11 pt text is condensed to about 10.6 pt
const MADDE_NOKTA_PUNTO = 13;
const SATIR_ARASI = 13.5;
const KIRMIZI = [238 / 255, 0, 0];
const IMZA_KUTUSU = { x: 347.6, ust: 622, en: 176.4, boy: 72.4 };

/** "2026-11-05" + "09:00" → "05/11/2026 09:00" (dd/mm/yyyy everywhere, #560); no date → "". */
function tarihSaat({ tarih, saat } = {}) {
  if (!tarih) return "";
  const [y, a, g] = tarih.split("-");
  return `${g}/${a}/${y}${saat ? ` ${saat}` : ""}`;
}

/** A phase as the cell lines: ["05/11/2026 09:00", "– 07/11/2026 18:00"]; nothing known → []. */
export function donemSatirlari(asama) {
  const ilk = tarihSaat(asama?.ilk);
  const son = tarihSaat(asama?.son);
  return [ilk, son ? `${ilk ? "– " : ""}${son}` : ""].filter(Boolean);
}

/** Letters a file name may keep: Turkish letters become their ASCII partner, everything else outside [A-Za-z0-9] becomes "-". */
export function dosyaParcasi(s) {
  const harita = { ç: "c", Ç: "C", ğ: "g", Ğ: "G", ı: "i", İ: "I", ö: "o", Ö: "O", ş: "s", Ş: "S", ü: "u", Ü: "U" };
  return String(s || "")
    .replace(/[çÇğĞıİöÖşŞüÜ]/g, (c) => harita[c])
    .replace(/[^A-Za-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

/** "ISG-Taahhutnamesi_ACME-Ltd_Nato-Edge-26_TR.pdf" / "OHS-Undertaking_..._EN.pdf" */
export function isgTaahhutnameDosyaAdi(veri, dil) {
  const t = i18n.getFixedT(dil, "pdf");
  const parcalar = [t("isgTaahhutname.belge.dosyaOnEki"), dosyaParcasi(veri.firmaAdi), dosyaParcasi(veri.fuarAdlari?.[dil] || veri.fuarAdlari?.tr), dil.toUpperCase()];
  return `${parcalar.filter(Boolean).join("_")}.pdf`;
}

/** PNG or JPEG by the file's own first bytes; anything else (webp, svg ...) → null (the caller converts first). */
export function resimTuru(bytes) {
  if (!bytes || bytes.length < 12) return null;
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) return "png";
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "jpg";
  return null;
}

/** Word wrap by measured width; "\n" breaks, a word wider than the cell is cut. */
function sar(font, metin, punto, genislik, sikistir = 1) {
  const bosluk = font.widthOfTextAtSize(" ", punto);
  const olcu = (satir) => font.widthOfTextAtSize(satir, punto) - (satir.split(" ").length - 1) * bosluk * (1 - sikistir);
  const satirlar = [];
  for (const parca of String(metin ?? "").split("\n")) {
    let satir = "";
    for (const kelime of parca.split(/\s+/).filter(Boolean)) {
      let k = kelime;
      while (font.widthOfTextAtSize(k, punto) > genislik) {
        let n = k.length - 1;
        while (n > 1 && font.widthOfTextAtSize(k.slice(0, n), punto) > genislik) n -= 1;
        if (satir) { satirlar.push(satir); satir = ""; }
        satirlar.push(k.slice(0, n));
        k = k.slice(n);
      }
      const deneme = satir ? `${satir} ${k}` : k;
      if (satir && olcu(deneme) > genislik) { satirlar.push(satir); satir = k; } else satir = deneme;
    }
    satirlar.push(satir);
  }
  return satirlar;
}

/** The largest size ≤ `punto` (down to `enAz`) at which `metin` fits on one line of `genislik`. */
function sigdir(font, metin, punto, genislik, enAz = 7) {
  let p = punto;
  while (p > enAz && font.widthOfTextAtSize(metin, p) > genislik) p -= 0.5;
  return p;
}

/**
 * Builds the PDF and returns its bytes.
 * @param {object} veri see the top of the file
 * @param {"tr"|"en"} dil the language of the labels and the undertaking (the fair name follows it too, Turkish when the fair has no English name)
 * @param {{font: Uint8Array, sakaLogo: Uint8Array}} varliklar
 * @returns {Promise<Uint8Array>}
 */
export async function isgTaahhutnamePdfUret(veri, dil, varliklar) {
  if (!ISG_TAAHHUTNAME_DILLERI.includes(dil)) throw new Error(`Unsupported language: ${dil}`);
  const [{ PDFDocument, rgb }, fontkitModulu] = await Promise.all([import("pdf-lib"), import("@pdf-lib/fontkit")]);
  const t = i18n.getFixedT(dil, "pdf");

  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkitModulu.default || fontkitModulu);
  doc.setTitle(`${t("isgTaahhutname.belge.baslik")} - ${veri.firmaAdi || ""}`.replace(/ - $/, ""));
  doc.setProducer("FDS");
  doc.setLanguage(dil);
  doc.setCreationDate(new Date());

  const baglam = { doc, rgb, t, veri, dil, varliklar };
  if (dil === "tr") await yerliFormCiz(baglam);
  else await yabanciFormCiz(baglam);
  return doc.save();
}

// ── "tr": the YERLİ FİRMA template ──

// geometry in points from the TOP-left, converted from the template's PDF numbers (A4, baselines measured with pdf.js)
const YERLI = {
  fuarLogo: { x: 62.1, ust: 35.4, en: 107.6, boy: 107.6 }, // the template's logo image is a square with white margins; the logo sits in its middle
  sakaLogo: { x: 422.7, ust: 59.7, en: 94.1, boy: 46.7 }, // places the visible SAKA GRUP mark where the template's (padded) image shows it
  baslikTaban: 186,
  baslikPunto: 16.1,
  tabloSol: 56.85,
  kolAyraci: 217.45,
  tabloSag: 539.25,
  tabloUst: 203.5,
  satirYukseklikleri: [37.95, 36.9],
  hucreYatay: 5.55,
  metinSol: 56.6,
  metinSag: 538.5,
  punto: 12,
  satirAraligi: 15.85,
  paragrafAraligi: 21.85, // baseline to baseline between two paragraphs (a line plus 6 pt)
  ilkPragrafTaban: 315.6,
  maddeOk: 74.6,
  maddeMetin: 92.6,
  imzaX: [56.6, 233.6, 410.6],
  imzaAraligi: 41.8,
  altCizgi: { x1: 53.3, x2: 517.2, ust: 764.5 },
  altPunto: 10.1,
  altTabanlar: [781.7, 793.2, 804.7, 816.2], // organizer name, organizer address (both fillable), then the two SAKA GRUP lines
  altSinir: 752, // a baseline below this moves to a new page (the letterhead rule is at 764.5)
};
const SIKISTIRMA = 0.85; // how far a justified line may squeeze its spaces before the next word goes down a line (the template's tightest line is at 87%)
const GUN_SAYISI_SINIRI = 7; // a phase longer than this is written as a range ("20-29 Eylül 2026") instead of every day
const AY_YEREL = { tr: "tr-TR", en: "en-GB" };

/** The Turkish locative after a year read aloud ("2026'da", "2027'de", "2025'te", "2040'ta"): by the last word of the number. */
export function yilEki(yil) {
  const son = Math.abs(Number(yil)) % 10;
  if (son) return { 1: "de", 2: "de", 3: "te", 4: "te", 5: "te", 6: "da", 7: "de", 8: "de", 9: "da" }[son];
  return { 0: "de", 1: "da", 2: "de", 3: "da", 4: "ta", 5: "de", 6: "ta", 7: "te", 8: "de", 9: "da" }[Math.floor((Math.abs(Number(yil)) % 100) / 10)];
}

const gunMs = (tarih) => { const [y, a, g] = tarih.split("-").map(Number); return Date.UTC(y, a - 1, g); };

/**
 * A phase as the running text writes its days: "20-21-22 Eylül 2026" (every day, the template's way), "30-31 Ekim, 1-2 Kasım 2026" across
 * months, "20-29 Eylül 2026" past a week. { metin, yil } with `yil` the last year (the Turkish suffix depends on it); null when no date is known.
 */
export function donemGunleri(asama, dil = "tr") {
  const ilk = asama?.ilk?.tarih;
  const son = asama?.son?.tarih;
  if (!ilk && !son) return null;
  const bas = gunMs(ilk || son);
  const bit = Math.max(bas, gunMs(son || ilk));
  const ay = new Intl.DateTimeFormat(AY_YEREL[dil] || AY_YEREL.tr, { month: "long", timeZone: "UTC" });
  const bilgi = (ms) => { const d = new Date(ms); return { yil: d.getUTCFullYear(), ay: ay.format(d), g: d.getUTCDate() }; };
  const sayi = Math.round((bit - bas) / 86400000) + 1;
  if (sayi > GUN_SAYISI_SINIRI) {
    const a = bilgi(bas);
    const z = bilgi(bit);
    if (a.yil === z.yil && a.ay === z.ay) return { metin: `${a.g}-${z.g} ${z.ay} ${z.yil}`, yil: z.yil };
    return { metin: `${a.g} ${a.ay}${a.yil === z.yil ? "" : ` ${a.yil}`} - ${z.g} ${z.ay} ${z.yil}`, yil: z.yil };
  }
  const gruplar = [];
  for (let ms = bas; ms <= bit; ms += 86400000) {
    const b = bilgi(ms);
    const son2 = gruplar[gruplar.length - 1];
    if (son2 && son2.yil === b.yil && son2.ay === b.ay) son2.gunler.push(b.g);
    else gruplar.push({ yil: b.yil, ay: b.ay, gunler: [b.g] });
  }
  const metin = gruplar
    .map((g, i) => `${g.gunler.join("-")} ${g.ay}${i === gruplar.length - 1 || gruplar[i + 1].yil !== g.yil ? ` ${g.yil}` : ""}`)
    .join(", ");
  return { metin, yil: gruplar[gruplar.length - 1].yil };
}

/** "Sign İstanbul 2026 Fuarı" → "Sign İstanbul 2026": the template adds the word "Fuarı" itself, so a name that already ends in it must not say it twice. */
export const fuarAdiKoku = (ad) => String(ad || "").replace(/\s+fuar[ıi]?\s*$/i, "").trim();

/** "5" + ["512","514"] → "5 / 512, 514"; several halls "5 / 512; 6 / 601"; stands not tied to a hall are just listed. */
export function salonStantMetni(standlar) {
  const gruplar = new Map();
  for (const s of standlar || []) {
    const hol = String(s.hol || "").trim();
    if (!gruplar.has(hol)) gruplar.set(hol, []);
    if (s.kod && !gruplar.get(hol).includes(s.kod)) gruplar.get(hol).push(s.kod);
  }
  return [...gruplar.entries()].map(([hol, kodlar]) => [hol, kodlar.join(", ")].filter(Boolean).join(" / ")).filter(Boolean).join("; ");
}

async function yerliFormCiz({ doc, rgb, t, veri, dil, varliklar }) {
  if (!varliklar.fontKalin || !varliklar.fontItalik) throw new Error("The Turkish form needs the bold and italic font files");
  const m = (anahtar, degerler) => t(`isgTaahhutname.yerli.${anahtar}`, degerler);
  const font = await doc.embedFont(varliklar.font, { subset: true });
  const kalin = await doc.embedFont(varliklar.fontKalin, { subset: true });
  // the italic font also draws what people type into the footer fields: whole, so a letter the form did not print still has its glyph
  const italik = await doc.embedFont(varliklar.fontItalik, { subset: false });
  let sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);
  const y = (ust) => SAYFA.boy - ust;
  const siyah = rgb(0, 0, 0);
  const punto = YERLI.punto;
  const yaz = (f, metin, x, taban, p = punto) => sayfa.drawText(metin, { x, y: y(taban), size: p, font: f, color: siyah });

  // ── logos ──
  const sigdirResim = (en, boy, kutu) => { const olcek = Math.min(kutu.en / en, kutu.boy / boy); return { w: en * olcek, h: boy * olcek }; };
  const saka = await doc.embedPng(varliklar.sakaLogo);
  sayfa.drawImage(saka, { x: YERLI.sakaLogo.x, y: y(YERLI.sakaLogo.ust + YERLI.sakaLogo.boy), width: YERLI.sakaLogo.en, height: YERLI.sakaLogo.boy });
  const logoTuru = resimTuru(veri.logo);
  if (logoTuru) {
    const logo = logoTuru === "png" ? await doc.embedPng(veri.logo) : await doc.embedJpg(veri.logo);
    const b = sigdirResim(logo.width, logo.height, YERLI.fuarLogo);
    const kutu = YERLI.fuarLogo;
    sayfa.drawImage(logo, { x: kutu.x + (kutu.en - b.w) / 2, y: y(kutu.ust + (kutu.boy + b.h) / 2), width: b.w, height: b.h });
  }

  // ── title ──
  const baslik = m("baslik");
  yaz(kalin, baslik, (SAYFA.en - kalin.widthOfTextAtSize(baslik, YERLI.baslikPunto)) / 2, YERLI.baslikTaban, YERLI.baslikPunto);

  // ── table: bold caption | value (wrapped, the row grows with it) ──
  const hucreGenislik = YERLI.tabloSag - YERLI.kolAyraci - 2 * YERLI.hucreYatay;
  const satirYuk = punto * 1.2;
  let ustte = YERLI.tabloUst;
  [[m("firmaAdi"), veri.firmaAdi || ""], [m("salonStant"), salonStantMetni(veri.standlar)]].forEach(([etiket, deger], i) => {
    const satirlar = sar(font, deger, punto, hucreGenislik);
    const yukseklik = Math.max(YERLI.satirYukseklikleri[i], satirlar.length * satirYuk + 16);
    const sol = [YERLI.tabloSol, YERLI.kolAyraci, YERLI.tabloSag];
    for (let k = 0; k < 2; k += 1) {
      sayfa.drawRectangle({ x: sol[k], y: y(ustte + yukseklik), width: sol[k + 1] - sol[k], height: yukseklik, borderColor: siyah, borderWidth: 0.5 });
    }
    const ilkTaban = (yukseklik - satirlar.length * satirYuk) / 2 + 11.5;
    yaz(kalin, etiket, YERLI.tabloSol + YERLI.hucreYatay, ustte + (yukseklik - satirYuk) / 2 + 11.5);
    satirlar.forEach((s, k) => { if (s) yaz(font, s, YERLI.kolAyraci + YERLI.hucreYatay, ustte + ilkTaban + k * satirYuk); });
    ustte += yukseklik;
  });

  // the fair's organizer firma(s), "A / B"; empty when the fair has none (the paragraph then says "Fuar organizatörü")
  const organizator = (veri.organizatorAdlari || []).filter(Boolean).join(" / ");

  // ── footer (letterhead): the rule, two FILLABLE lines for the organizer (name pre-filled when known), the SAKA GRUP lines ──
  function altBilgiCiz() {
    sayfa.drawLine({ start: { x: YERLI.altCizgi.x1, y: y(YERLI.altCizgi.ust) }, end: { x: YERLI.altCizgi.x2, y: y(YERLI.altCizgi.ust) }, thickness: 1, color: siyah });
    const form = doc.getForm();
    ["organizatorAdi", "organizatorAdresi"].forEach((ad, i) => {
      const alan = form.createTextField(ad);
      alan.addToPage(sayfa, { x: YERLI.metinSol - 2, y: y(YERLI.altTabanlar[i] + 4), width: YERLI.altCizgi.x2 - YERLI.metinSol, height: 14, borderWidth: 0, font: italik, textColor: siyah });
      alan.setFontSize(YERLI.altPunto);
      if (i === 0 && organizator) alan.setText(organizator); // the organizer's name is known (the fair's organizer firma); the address is not stored anywhere
    });
    form.updateFieldAppearances(italik); // pdf-lib would otherwise add its own Helvetica for them at save
    yaz(italik, m("altBilgi1"), YERLI.metinSol, YERLI.altTabanlar[2], YERLI.altPunto);
    yaz(italik, m("altBilgi2"), YERLI.metinSol, YERLI.altTabanlar[3], YERLI.altPunto);
  }
  altBilgiCiz();

  // ── the running text: justified, a line of 15.85 pt, paragraphs 6 pt apart ──
  const genislik = YERLI.metinSag - YERLI.metinSol;
  let taban = YERLI.ilkPragrafTaban;
  const satirBaslat = () => {
    if (taban <= YERLI.altSinir) return;
    sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);
    taban = 60;
  };
  // one justified line: every word on its own x, the spaces stretched to the line's width (the last line of a paragraph stays left)
  const satirYaz = (satir, x, sagSinir, sonSatir) => {
    const kelimeler = satir.split(" ");
    if (sonSatir || kelimeler.length < 2) { yaz(font, satir, x, taban); return; }
    const toplam = kelimeler.reduce((s, k) => s + font.widthOfTextAtSize(k, punto), 0);
    const bosluk = (sagSinir - x - toplam) / (kelimeler.length - 1);
    let sol = x;
    for (const k of kelimeler) { yaz(font, k, sol, taban); sol += font.widthOfTextAtSize(k, punto) + bosluk; }
  };
  const paragraf = (metin, aralik) => {
    taban += aralik;
    const satirlar = sar(font, metin, punto, genislik, SIKISTIRMA);
    satirlar.forEach((s, i) => { satirBaslat(); satirYaz(s, YERLI.metinSol, YERLI.metinSag, i === satirlar.length - 1); taban += YERLI.satirAraligi; });
    taban -= YERLI.satirAraligi;
  };
  const madde = (metin, aralik) => {
    taban += aralik;
    const satirlar = sar(font, metin, punto, YERLI.metinSag - YERLI.maddeMetin, SIKISTIRMA);
    satirlar.forEach((s, i) => {
      satirBaslat();
      if (i === 0) sayfa.drawSvgPath("M 0 0 L 9.2 4.1 L 0 8.2 L 2.6 4.1 Z", { x: YERLI.maddeOk, y: y(taban - 8.6), color: siyah, borderWidth: 0 });
      satirYaz(s, YERLI.maddeMetin, YERLI.metinSag, i === satirlar.length - 1);
      taban += YERLI.satirAraligi;
    });
    taban -= YERLI.satirAraligi;
  };

  // paragraph 1 names the fair, the dates of its three phases and its venue; a part the fair does not have is left out of the sentence
  const gunler = (anahtar) => donemGunleri(veri.asamalar?.[anahtar], dil);
  const ek = (g) => (dil === "tr" ? yilEki(g.yil) : "");
  const gKurulum = gunler("kurulum");
  const gFuar = gunler("fuar");
  const gSokum = gunler("sokum");
  const cumle = m("paragraf1", {
    kurulum: gKurulum ? m("kurulum", { gunler: gKurulum.metin }) : "",
    fuar: gFuar ? m("fuar", { gunler: gFuar.metin, ek: ek(gFuar) }) : "",
    sokum: gSokum ? m(gKurulum || gFuar ? "sokumIse" : "sokum", { gunler: gSokum.metin, ek: ek(gSokum) }) : "",
    alan: veri.fuarAlani ? m("alan", { ad: veri.fuarAlani }) : m("alanAdsiz"),
    fuarAdi: fuarAdiKoku(veri.fuarAdlari?.[dil] || veri.fuarAdlari?.tr),
  });
  taban -= YERLI.paragrafAraligi;
  paragraf(cumle, YERLI.paragrafAraligi);
  paragraf(m("paragraf2"), YERLI.paragrafAraligi);
  paragraf(m("paragraf3"), YERLI.paragrafAraligi);
  ["madde1", "madde2", "madde3", "madde4"].forEach((a, i) => madde(m(a), i === 0 ? YERLI.paragrafAraligi : YERLI.satirAraligi));
  paragraf(m("paragraf4", { organizator: organizator || m("organizatorGenel") }), YERLI.paragrafAraligi);

  // ── the three signature captions, 41.8 pt under the text; a new page when the text came down to the letterhead ──
  taban += YERLI.imzaAraligi;
  if (taban > YERLI.altSinir - 12) { sayfa = doc.addPage([SAYFA.en, SAYFA.boy]); taban = 100; }
  ["imza1", "imza2", "imza3"].forEach((a, i) => yaz(kalin, m(a), YERLI.imzaX[i], taban));
}

// ── "en": the Yabancı Katılımcı template ──
async function yabanciFormCiz({ doc, rgb, t, veri, dil, varliklar }) {
  const m = (anahtar) => t(`isgTaahhutname.belge.${anahtar}`);
  const font = await doc.embedFont(varliklar.font, { subset: true });
  let sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);

  // pdf-lib's origin is bottom-left; everything below is measured from the top.
  const y = (ust) => SAYFA.boy - ust;
  const siyah = rgb(0, 0, 0);
  const kirmizi = rgb(...KIRMIZI);
  const yaz = (metin, x, taban, punto, renk = siyah) => sayfa.drawText(metin, { x, y: y(taban), size: punto, font, color: renk });

  // ── logos ──
  const resimSigdir = (en, boy, kutu) => {
    const olcek = Math.min(kutu.en / en, kutu.boy / boy);
    return { w: en * olcek, h: boy * olcek };
  };
  const sakaLogo = await doc.embedPng(varliklar.sakaLogo);
  const sakaBoyut = resimSigdir(sakaLogo.width, sakaLogo.height, SAKA_LOGO);
  sayfa.drawImage(sakaLogo, { x: SAKA_LOGO.x, y: y(SAKA_LOGO.ust + sakaBoyut.h), width: sakaBoyut.w, height: sakaBoyut.h });
  const logoTuru = resimTuru(veri.logo);
  if (logoTuru) {
    const logo = logoTuru === "png" ? await doc.embedPng(veri.logo) : await doc.embedJpg(veri.logo);
    const boyut = resimSigdir(logo.width, logo.height, FUAR_LOGO);
    sayfa.drawImage(logo, { x: FUAR_LOGO.x, y: y(FUAR_LOGO.ust + boyut.h), width: boyut.w, height: boyut.h });
  }

  // ── tables: rows grow with their wrapped text; a cell is { kol: [from, to), satirlar | metin, punto, sigdir } ──
  const kolX = (i) => KOL_SOL + i * KOL_GENISLIK;
  function tabloCiz(ust, satirlar) {
    let ustte = ust;
    for (const satir of satirlar) {
      const hucreler = satir.map((h) => {
        const genislik = (h.kol[1] - h.kol[0]) * KOL_GENISLIK - 2 * HUCRE_YATAY;
        if (h.satirlar) {
          // phase cells: one line per entry, all at the size the widest one fits at (never wrapped)
          const punto = h.satirlar.reduce((en, s) => Math.min(en, sigdir(font, s, TABLO_PUNTO - 1, genislik, 6.5)), TABLO_PUNTO - 1);
          return { ...h, punto };
        }
        // a label shrinks to stay on one line (it wraps only below the smallest size); a value (fair, company, stands) wraps
        const punto = h.sigdir ? sigdir(font, h.metin, TABLO_PUNTO, genislik) : TABLO_PUNTO;
        return { ...h, punto, satirlar: sar(font, h.metin, punto, genislik) };
      });
      const yukseklik = Math.max(HUCRE_EN_AZ, ...hucreler.map((h) => h.satirlar.length * h.punto * 1.2 + 2 * HUCRE_DIKEY));
      const satirUst = ustte;
      for (const h of hucreler) {
        const x0 = kolX(h.kol[0]);
        sayfa.drawRectangle({ x: x0, y: y(satirUst + yukseklik), width: kolX(h.kol[1]) - x0, height: yukseklik, borderColor: siyah, borderWidth: 0.5 });
        h.satirlar.forEach((s, i) => { if (s) yaz(s, x0 + HUCRE_YATAY, satirUst + HUCRE_DIKEY + h.punto * 0.92 + i * h.punto * 1.2, h.punto); });
      }
      ustte += yukseklik;
    }
    return ustte;
  }

  const fuarAdi = veri.fuarAdlari?.[dil] || veri.fuarAdlari?.tr || "";
  const tablo1Alt = tabloCiz(TABLO1_UST, [
    [{ kol: [0, 1], metin: m("etkinlikAdi"), sigdir: true }, { kol: [1, 4], metin: fuarAdi }],
    [
      { kol: [0, 1], metin: "" },
      { kol: [1, 2], metin: m("kurulumDonemi"), sigdir: true },
      { kol: [2, 3], metin: m("etkinlikDonemi"), sigdir: true },
      { kol: [3, 4], metin: m("sokumDonemi"), sigdir: true },
    ],
    [
      { kol: [0, 1], metin: m("toplamEtkinlikSuresi"), sigdir: true },
      { kol: [1, 2], satirlar: donemSatirlari(veri.asamalar?.kurulum) },
      { kol: [2, 3], satirlar: donemSatirlari(veri.asamalar?.fuar) },
      { kol: [3, 4], satirlar: donemSatirlari(veri.asamalar?.sokum) },
    ],
  ]);
  const tablo2Alt = tabloCiz(tablo1Alt + TABLO_ARASI, [
    [{ kol: [0, 1], metin: m("katilimciFirma"), sigdir: true }, { kol: [1, 4], metin: veri.firmaAdi || "" }],
    [
      { kol: [0, 1], metin: m("holNo"), sigdir: true },
      { kol: [1, 2], metin: (veri.holAdlari || []).join(", ") },
      { kol: [2, 3], metin: m("standNo"), sigdir: true },
      { kol: [3, 4], metin: (veri.standKodlari || []).join(", ") },
    ],
  ]);

  // ── the undertaking: baselines follow the template's spacing (paragraph 28 pt, bullet 38 pt, acknowledgement 29 pt) ──
  const SOL = 71.4;
  const GENISLIK = SAG - SOL;
  let taban = tablo2Alt + 38.7;
  const paragraf = (metin, aralik) => {
    taban += aralik;
    for (const s of sar(font, metin, METIN_PUNTO, GENISLIK)) { yaz(s, SOL, taban, METIN_PUNTO); taban += SATIR_ARASI; }
    taban -= SATIR_ARASI;
  };
  taban -= 28; // the first paragraph adds its own 28 pt below
  paragraf(m("paragraf1"), 28);
  paragraf(m("paragraf2"), 27);
  paragraf(m("paragraf3"), 28);
  const MADDE_X = 89;
  const MADDE_METIN_X = 107;
  ["madde1", "madde2", "madde3"].forEach((anahtar, i) => {
    taban += i === 0 ? 29 : 38;
    yaz("•", MADDE_X, taban, MADDE_NOKTA_PUNTO, kirmizi);
    const satirlar = sar(font, m(anahtar), METIN_PUNTO, SAG - MADDE_METIN_X);
    satirlar.forEach((s, k) => yaz(s, MADDE_METIN_X, taban + k * 14.5, METIN_PUNTO, kirmizi));
    taban += (satirlar.length - 1) * 14.5;
  });
  if (m("taahhutSonu")) paragraf(m("taahhutSonu"), 29);
  paragraf(m("kabul"), 29);
  const kabulTabani = taban;

  // ── signature box: where the template has it, lower when a long table pushed the text down, on a new page when there is no room ──
  let imzaUst = Math.max(IMZA_KUTUSU.ust, kabulTabani + 78);
  let etiketTaban = imzaUst - 12;
  if (imzaUst + IMZA_KUTUSU.boy > SAYFA.boy - 36) {
    sayfa = doc.addPage([SAYFA.en, SAYFA.boy]);
    imzaUst = 120;
    etiketTaban = imzaUst - 12;
  }
  // the label is as wide as the box in the template (9.8 pt there), so it shrinks to the box instead of running past it
  const imzaPunto = sigdir(font, m("imza"), METIN_PUNTO, IMZA_KUTUSU.en - 4.8);
  yaz(m("imza"), IMZA_KUTUSU.x + 2.4, etiketTaban, imzaPunto);
  sayfa.drawRectangle({ x: IMZA_KUTUSU.x, y: y(imzaUst + IMZA_KUTUSU.boy), width: IMZA_KUTUSU.en, height: IMZA_KUTUSU.boy, borderColor: siyah, borderWidth: 0.5 });
}
